/**
 * Register upstream audio for a network renderer consumer (LAN host, not loopback).
 */

import { QueueController } from './QueueController';
import {
  classifyStreamHostKind,
  isLocalLibraryTrack,
} from '../lib/playback-bridge';
import { detectPlatform } from '../utils/platform';
import type { ZoneRow } from './zoneRestorePick';

export async function registerHandoffStreamForNetwork(
  upstreamUrl: string,
  zone: ZoneRow,
  track: any,
  formatId: number,
): Promise<{ url: string; formatId: number }> {
  const renderer = { id: zone.id, name: zone.name || zone.id, manufacturer: String(zone.metadata?.manufacturer || '') };
  let url = upstreamUrl;
  let resolvedFormatId = formatId;

  if (isLocalLibraryTrack(track) && track.file_path && detectPlatform().isTauriDesktop) {
    const { isConnectorAvailable, getProxyInfo } = await import('../lib/connector');
    if (isConnectorAvailable()) {
      const info = await getProxyInfo();
      if (!info?.host_ip || info.host_ip === '127.0.0.1') {
        throw new Error('[zone-transfer] LAN host unavailable for network handoff');
      }
      console.log('[zone-transfer] register local file for network consumer', zone.id, info.host_ip);
    }
  } else if (url.startsWith('https://') || classifyStreamHostKind(url) !== 'lan_ip') {
    try {
      const resolved = await QueueController.resolveStreamUrl(track, renderer);
      if (resolved?.url) {
        url = resolved.url;
        resolvedFormatId = resolved.formatId || formatId;
      }
    } catch { /* use cached url */ }
  }

  if (detectPlatform().isTauriDesktop) {
    const { isConnectorAvailable, getProxyInfo } = await import('../lib/connector');
    if (isConnectorAvailable() && (url.startsWith('https://') || isLocalLibraryTrack(track))) {
      const info = await getProxyInfo();
      console.log('[zone-transfer] register stream for network consumer', {
        rendererId: zone.id,
        hostIp: info?.host_ip,
        upstream: url.slice(0, 80),
      });
    }
  }

  const hostKind = classifyStreamHostKind(url);
  if (hostKind === 'loopback') {
    console.warn('[zone-transfer] refusing loopback URL for network renderer handoff');
  }

  return { url, formatId: resolvedFormatId };
}
